import React, { useState, useEffect } from 'react';
import DashboardLayout from '../layouts/DashboardLayout';
import { api } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  ClipboardList,
  Volume2,
  Mic,
  Calendar as CalendarIcon,
  Loader2,
  Film,
  PenLine,
  Sparkles
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AnalyticsPage = () => {
  const { theme } = useTheme();
  const isDark = theme?.isDark;
  
  const [period, setPeriod] = useState('monthly');
  const [dailyData, setDailyData] = useState([]);
  const [moduleData, setModuleData] = useState([]);
  const [studentData, setStudentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date()
  });

  useEffect(() => {
    fetchAnalytics();
  }, [period, dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');
      
      const [dailyRes, moduleRes, studentRes] = await Promise.all([
        api.get(`/analytics/daily?start_date=${startDate}&end_date=${endDate}`),
        api.get(`/analytics/module-performance?period=${period}`),
        api.get('/analytics/student-progress')
      ]);
      
      setDailyData(dailyRes.data);
      setModuleData(moduleRes.data);
      setStudentData(studentRes.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#f97316', '#8b5cf6'];

  const moduleIcons = {
    tts: Volume2,
    stt: Mic,
    mcq: ClipboardList,
    ftb: PenLine,
    vmcq: Film
  };

  const moduleNames = {
    tts: 'Text to Speech',
    stt: 'Speech to Text',
    mcq: 'MCQ Quiz',
    ftb: 'Fill the Blanks',
    vmcq: 'Cinematic MCQ'
  };

  const moduleColors = {
    tts: '#06b6d4',    // Cyan
    stt: '#10b981',    // Emerald Green
    mcq: '#eab308',    // Yellow
    ftb: '#f43f5e',    // Rose/Pink
    vmcq: '#8b5cf6'    // Purple
  };

  const moduleGradients = {
    tts: ['#22d3ee', '#0891b2'],
    stt: ['#34d399', '#059669'],
    mcq: ['#fde047', '#ca8a04'],
    ftb: ['#fb7185', '#e11d48'],
    vmcq: ['#a78bfa', '#7c3aed']
  };

  // Prepare chart data
  const chartData = dailyData.map(d => ({
    date: d.date?.split('-').slice(1).join('/') || '',
    attempts: d.total?.attempts || 0,
    tts: d.tts?.attempts || 0,
    stt: d.stt?.attempts || 0,
    mcq: d.mcq?.attempts || 0,
    ftb: d.ftb?.attempts || 0,
    vmcq: d.vmcq?.attempts || 0
  })).reverse();

  const pieData = moduleData.map(m => ({
    name: moduleNames[m.module_type] || m.module_type,
    value: m.total_attempts || 0,
    avgScore: Math.round(m.average_score || 0),
    color: moduleColors[m.module_type] || '#8b5cf6',
    gradient: moduleGradients[m.module_type] || ['#8b5cf6', '#7c3aed'],
    moduleType: m.module_type
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className={`h-8 w-8 animate-spin ${isDark ? 'text-violet-400' : 'text-indigo-600'}`} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Analytics Dashboard
            </h1>
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Track performance and engagement metrics
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Period Filter */}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className={`w-32 ${isDark ? 'bg-white/10 border-white/20 text-white' : ''}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Range Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={`${isDark ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : ''}`}>
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {format(dateRange.from, 'MMM dd')} - {format(dateRange.to, 'MMM dd')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => range && setDateRange(range)}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Summary Cards - Always show all 5 modules */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { key: 'tts', name: 'Text to Speech', Icon: Volume2, bgColor: 'from-cyan-500/20 to-blue-500/20', iconColor: 'text-cyan-400' },
            { key: 'stt', name: 'Speech to Text', Icon: Mic, bgColor: 'from-emerald-500/20 to-teal-500/20', iconColor: 'text-emerald-400' },
            { key: 'mcq', name: 'MCQ Quiz', Icon: ClipboardList, bgColor: 'from-yellow-500/20 to-amber-500/20', iconColor: 'text-yellow-400' },
            { key: 'ftb', name: 'Fill the Blanks', Icon: PenLine, bgColor: 'from-rose-500/20 to-pink-500/20', iconColor: 'text-rose-400' },
            { key: 'vmcq', name: 'Cinematic MCQ', Icon: Film, bgColor: 'from-purple-500/20 to-violet-500/20', iconColor: 'text-purple-400' }
          ].map((module) => {
            const data = moduleData.find(m => m.module_type === module.key);
            const attempts = data?.total_attempts || 0;
            const avgScore = Math.round(data?.average_score || 0);
            const hasData = attempts > 0;
            
            return (
              <Card key={module.key} className={`overflow-hidden ${isDark ? 'premium-card' : 'border-2 border-slate-200'} ${!hasData ? 'opacity-60' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${module.bgColor}`}>
                      <module.Icon className={`h-6 w-6 ${module.iconColor}`} />
                    </div>
                    <div>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {module.name}
                      </p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {attempts}
                      </p>
                      <p className={`text-sm font-medium ${
                        !hasData ? (isDark ? 'text-slate-500' : 'text-slate-400') :
                        avgScore >= 80 ? 'text-emerald-500' :
                        avgScore >= 50 ? 'text-amber-500' :
                        'text-rose-500'
                      }`}>
                        {hasData ? `${avgScore}% avg score` : 'No data'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Activity Chart */}
          <Card className={`overflow-hidden ${isDark ? 'premium-card border-violet-500/20' : 'border-2 border-slate-200'}`}>
            <CardHeader className={`${isDark ? 'bg-gradient-to-r from-violet-900/30 to-indigo-900/30 border-b border-violet-500/20' : 'bg-gradient-to-r from-violet-50 to-indigo-50 border-b border-slate-200'}`}>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                  <BarChart3 className="h-5 w-5 text-violet-500" />
                </div>
                <span>Daily Activity</span>
                <Sparkles className={`h-4 w-4 ml-auto ${isDark ? 'text-violet-400' : 'text-violet-500'}`} />
              </CardTitle>
            </CardHeader>
            <CardContent className={`pt-6 ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} barGap={2}>
                    <defs>
                      <linearGradient id="ttsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22d3ee" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#0891b2" stopOpacity={0.9}/>
                      </linearGradient>
                      <linearGradient id="sttGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#059669" stopOpacity={0.9}/>
                      </linearGradient>
                      <linearGradient id="mcqGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fde047" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#ca8a04" stopOpacity={0.9}/>
                      </linearGradient>
                      <linearGradient id="ftbGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fb7185" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#e11d48" stopOpacity={0.9}/>
                      </linearGradient>
                      <linearGradient id="vmcqGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a78bfa" stopOpacity={1}/>
                        <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.9}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} opacity={0.5} />
                    <XAxis 
                      dataKey="date" 
                      stroke={isDark ? '#9ca3af' : '#6b7280'} 
                      tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: isDark ? '#4b5563' : '#d1d5db' }}
                    />
                    <YAxis 
                      stroke={isDark ? '#9ca3af' : '#6b7280'} 
                      tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: isDark ? '#4b5563' : '#d1d5db' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDark ? '#1f2937' : '#fff',
                        border: isDark ? '1px solid #4b5563' : '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
                      }}
                      labelStyle={{ color: isDark ? '#f3f4f6' : '#111827', fontWeight: 'bold', marginBottom: '8px' }}
                      itemStyle={{ color: isDark ? '#d1d5db' : '#374151' }}
                      cursor={{ fill: isDark ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)' }}
                      itemSorter={(item) => {
                        const order = { TTS: 1, STT: 2, MCQ: 3, FTB: 4, VMCQ: 5 };
                        return order[item.name] || 6;
                      }}
                    />
                    <Bar dataKey="tts" name="TTS" fill="url(#ttsGradient)" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="stt" name="STT" fill="url(#sttGradient)" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="mcq" name="MCQ" fill="url(#mcqGradient)" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="ftb" name="FTB" fill="url(#ftbGradient)" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="vmcq" name="VMCQ" fill="url(#vmcqGradient)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No data for selected period</p>
                </div>
              )}
              {/* Legend */}
              <div className="flex flex-wrap justify-center gap-4 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                {[
                  { key: 'tts', name: 'TTS', color: '#22d3ee' },
                  { key: 'stt', name: 'STT', color: '#34d399' },
                  { key: 'mcq', name: 'MCQ', color: '#fde047' },
                  { key: 'ftb', name: 'FTB', color: '#fb7185' },
                  { key: 'vmcq', name: 'VMCQ', color: '#a78bfa' }
                ].map(item => (
                  <div key={item.key} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: item.color }} />
                    <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{item.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Module Distribution */}
          <Card className={`overflow-hidden ${isDark ? 'premium-card border-emerald-500/30' : 'border-2 border-slate-200'}`}>
            <CardHeader className={`${isDark ? 'bg-gradient-to-r from-emerald-900/40 to-cyan-900/40 border-b border-emerald-500/30' : 'bg-gradient-to-r from-emerald-50 to-cyan-50 border-b border-slate-200'}`}>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-gradient-to-br from-emerald-500/30 to-cyan-500/30' : 'bg-gradient-to-br from-emerald-100 to-cyan-100'}`}>
                  <TrendingUp className={`h-5 w-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                </div>
                <span>Module Distribution</span>
                <Sparkles className={`h-4 w-4 ml-auto ${isDark ? 'text-emerald-400 animate-pulse' : 'text-emerald-500'}`} />
              </CardTitle>
            </CardHeader>
            <CardContent className={`pt-6 ${isDark ? 'bg-gradient-to-b from-slate-900/80 to-slate-900/40' : 'bg-white'}`}>
              {pieData.length > 0 && pieData.some(d => d.value > 0) ? (
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <defs>
                        <linearGradient id="pieGrad-tts" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#22d3ee" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#0891b2" stopOpacity={0.85}/>
                        </linearGradient>
                        <linearGradient id="pieGrad-stt" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#34d399" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#059669" stopOpacity={0.85}/>
                        </linearGradient>
                        <linearGradient id="pieGrad-mcq" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#fde047" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#ca8a04" stopOpacity={0.85}/>
                        </linearGradient>
                        <linearGradient id="pieGrad-ftb" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#fb7185" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#e11d48" stopOpacity={0.85}/>
                        </linearGradient>
                        <linearGradient id="pieGrad-vmcq" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#a78bfa" stopOpacity={1}/>
                          <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.85}/>
                        </linearGradient>
                      </defs>
                      <Pie
                        data={pieData.filter(d => d.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        stroke={isDark ? '#0f172a' : '#ffffff'}
                        strokeWidth={2}
                        animationBegin={0}
                        animationDuration={800}
                      >
                        {pieData.filter(d => d.value > 0).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={`url(#pieGrad-${entry.moduleType})`}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255,255,255,0.98)',
                          border: isDark ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid #e5e7eb',
                          borderRadius: '12px',
                          boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 20px rgba(0,0,0,0.1)',
                          padding: '12px 16px'
                        }}
                        formatter={(value, name, props) => [
                          <div key="content">
                            <div style={{ color: props.payload.color, fontWeight: 'bold', fontSize: '15px' }}>
                              {value} attempts
                            </div>
                            <div style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '12px', marginTop: '4px' }}>
                              Avg Score: <span style={{ color: props.payload.avgScore >= 80 ? '#34d399' : props.payload.avgScore >= 50 ? '#fbbf24' : '#f87171', fontWeight: 'bold' }}>{props.payload.avgScore}%</span>
                            </div>
                          </div>,
                          <span key="name" style={{ color: props.payload.color, fontWeight: 'bold', fontSize: '13px' }}>{name}</span>
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center">
                  <div className={`p-4 rounded-full ${isDark ? 'bg-slate-800' : 'bg-slate-100'} mb-4`}>
                    <TrendingUp className={`h-10 w-10 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                  </div>
                  <p className={`font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No data available</p>
                  <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Complete some tests to see distribution</p>
                </div>
              )}
              {/* Premium Legend - Always show all 5 modules */}
              {pieData.length > 0 && (
                <div className="grid grid-cols-5 gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700/50">
                  {[
                    { key: 'tts', name: 'TTS', color: '#22d3ee', gradient: ['#22d3ee', '#0891b2'] },
                    { key: 'stt', name: 'STT', color: '#34d399', gradient: ['#34d399', '#059669'] },
                    { key: 'mcq', name: 'MCQ', color: '#fde047', gradient: ['#fde047', '#ca8a04'] },
                    { key: 'ftb', name: 'FTB', color: '#fb7185', gradient: ['#fb7185', '#e11d48'] },
                    { key: 'vmcq', name: 'VMCQ', color: '#a78bfa', gradient: ['#a78bfa', '#7c3aed'] }
                  ].map((module) => {
                    const data = pieData.find(p => p.moduleType === module.key);
                    const hasData = data && data.value > 0;
                    return (
                      <div 
                        key={module.key} 
                        className={`flex flex-col items-center gap-1 px-2 py-3 rounded-xl transition-all ${
                          hasData ? 'hover:scale-105' : 'opacity-50'
                        } ${
                          isDark 
                            ? 'bg-gradient-to-r from-slate-800/80 to-slate-800/40 border border-slate-700/50 hover:border-slate-600' 
                            : 'bg-gradient-to-r from-slate-50 to-white border border-slate-200 hover:border-slate-300 hover:shadow-md'
                        }`}
                      >
                        <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-700'}`}>
                          {module.name}
                        </span>
                        <div 
                          className="w-4 h-4 rounded-full shadow-lg" 
                          style={{ 
                            background: `linear-gradient(135deg, ${module.gradient[0]}, ${module.gradient[1]})`,
                            boxShadow: `0 2px 8px ${module.color}40`
                          }} 
                        />
                        <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {hasData ? `${data.value} • ${data.avgScore}%` : 'No data'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Student Progress Table */}
        <Card className={`overflow-hidden ${isDark ? 'premium-card border-pink-500/20' : 'border-2 border-slate-200'}`}>
          <CardHeader className={`${isDark ? 'bg-gradient-to-r from-pink-900/30 to-rose-900/30 border-b border-pink-500/20' : 'bg-gradient-to-r from-pink-50 to-rose-50 border-b border-slate-200'}`}>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <div className={`p-2 rounded-lg ${isDark ? 'bg-pink-500/20' : 'bg-pink-100'}`}>
                <Users className="h-5 w-5 text-pink-500" />
              </div>
              <span>Student Progress</span>
              <Sparkles className={`h-4 w-4 ml-auto ${isDark ? 'text-pink-400' : 'text-pink-500'}`} />
            </CardTitle>
          </CardHeader>
          <CardContent className={`pt-0 ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
            {studentData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                      <th className={`text-left p-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Student</th>
                      <th className={`text-center p-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>TTS</th>
                      <th className={`text-center p-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>STT</th>
                      <th className={`text-center p-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>MCQ</th>
                      <th className={`text-center p-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>FTB</th>
                      <th className={`text-center p-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>VMCQ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentData.slice(0, 10).map((student, idx) => (
                      <tr key={idx} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-slate-100 hover:bg-slate-50'} transition-colors`}>
                        <td className={`p-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          <p className="font-medium">{student.name}</p>
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{student.email}</p>
                        </td>
                        <td className="text-center p-3">
                          {student.modules?.tts ? (
                            <div className={`inline-block px-2 py-1 rounded-lg ${isDark ? 'bg-cyan-500/10' : 'bg-cyan-50'}`}>
                              <p className={`font-medium ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                                {student.modules.tts.avg_score}%
                              </p>
                              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                {student.modules.tts.attempts} att
                              </p>
                            </div>
                          ) : (
                            <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>-</span>
                          )}
                        </td>
                        <td className="text-center p-3">
                          {student.modules?.stt ? (
                            <div className={`inline-block px-2 py-1 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                              <p className={`font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                {student.modules.stt.avg_score}%
                              </p>
                              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                {student.modules.stt.attempts} att
                              </p>
                            </div>
                          ) : (
                            <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>-</span>
                          )}
                        </td>
                        <td className="text-center p-3">
                          {student.modules?.mcq ? (
                            <div className={`inline-block px-2 py-1 rounded-lg ${isDark ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
                              <p className={`font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                                {student.modules.mcq.avg_score}%
                              </p>
                              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                {student.modules.mcq.attempts} att
                              </p>
                            </div>
                          ) : (
                            <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>-</span>
                          )}
                        </td>
                        <td className="text-center p-3">
                          {student.modules?.ftb ? (
                            <div className={`inline-block px-2 py-1 rounded-lg ${isDark ? 'bg-orange-500/10' : 'bg-orange-50'}`}>
                              <p className={`font-medium ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                                {student.modules.ftb.avg_score}%
                              </p>
                              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                {student.modules.ftb.attempts} att
                              </p>
                            </div>
                          ) : (
                            <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>-</span>
                          )}
                        </td>
                        <td className="text-center p-3">
                          {student.modules?.vmcq ? (
                            <div className={`inline-block px-2 py-1 rounded-lg ${isDark ? 'bg-purple-500/10' : 'bg-purple-50'}`}>
                              <p className={`font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                                {student.modules.vmcq.avg_score}%
                              </p>
                              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                {student.modules.vmcq.attempts} att
                              </p>
                            </div>
                          ) : (
                            <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                No student data available
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
